import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Loader,
  Center,
  Pagination,
  Accordion,
  Box,
  Timeline,
} from '@mantine/core';
import { Shield, ChevronDown, Activity } from 'react-feather';
import {
  authService,
  type ImpersonationLogEntry,
  type SessionActivity,
} from '../../../services/api/auth';
import { EmptyState } from '../../../components/common';

const PERSONA_LABELS: Record<string, { label: string; color: string }> = {
  internal_user: { label: 'Cohort Manager', color: 'blue' },
  coach: { label: 'Coach', color: 'green' },
  participant: { label: 'Participant', color: 'orange' },
  partner: { label: 'Partner', color: 'violet' },
};

const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
  cohort: { label: 'Cohort', color: 'blue' },
  task: { label: 'Task', color: 'teal' },
  session: { label: 'Session', color: 'violet' },
  user: { label: 'User', color: 'red' },
  sub_group: { label: 'Sub Group', color: 'orange' },
  coach: { label: 'Coach', color: 'green' },
};

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

function formatDuration(startedAt: string, endedAt: string | null): string {
  if (!endedAt) return 'Active';
  const ms = new Date(endedAt).getTime() - new Date(startedAt).getTime();
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

function SessionActivities({ sessionId }: { sessionId: string }) {
  const [activities, setActivities] = useState<SessionActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);
      try {
        const response = await authService.getSessionActivities(sessionId);
        setActivities(response.activities);
      } catch {
        setActivities([]);
      } finally {
        setLoading(false);
      }
    };
    fetchActivities();
  }, [sessionId]);

  if (loading) {
    return (
      <Center py="md">
        <Loader size="sm" />
      </Center>
    );
  }

  if (activities.length === 0) {
    return (
      <Text size="sm" c="dimmed" py="sm" ta="center">
        No actions recorded during this session
      </Text>
    );
  }

  return (
    <Timeline active={activities.length - 1} bulletSize={20} lineWidth={2} py="sm">
      {activities.map((activity) => {
        const sourceConfig = SOURCE_LABELS[activity.source] || {
          label: activity.source,
          color: 'gray',
        };

        return (
          <Timeline.Item key={activity.id} bullet={<Activity size={10} />}>
            <Group gap="xs" mb={2}>
              <Badge size="xs" variant="light" color={sourceConfig.color}>
                {sourceConfig.label}
              </Badge>
              <Badge size="xs" variant="light" color="blue">
                {activity.action_type}
              </Badge>
              <Text size="xs" c="dimmed">
                {formatTime(activity.created_at)}
              </Text>
            </Group>
            <Text size="sm">{activity.description}</Text>
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
}

export function ImpersonationLogPage() {
  const [entries, setEntries] = useState<ImpersonationLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25;

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authService.getImpersonationLog(page, limit);
      setEntries(response.entries);
      setTotalPages(response.pagination.totalPages);
      setTotal(response.pagination.total);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2} fw={700}>
              Impersonation Log
            </Title>
            <Text size="sm" c="dimmed">
              Audit trail of all admin impersonation sessions
            </Text>
          </div>
          {total > 0 && (
            <Badge variant="light" color="gray" size="lg">
              {total} {total === 1 ? 'session' : 'sessions'}
            </Badge>
          )}
        </Group>

        <Card withBorder radius="md" shadow="xs" p={0}>
          {loading ? (
            <Center py="xl">
              <Loader size="md" />
            </Center>
          ) : entries.length === 0 ? (
            <EmptyState
              icon={<Shield size={40} />}
              title="No impersonation sessions"
              description="No admin impersonation sessions have been recorded yet."
            />
          ) : (
            <>
              {/* Column headers — right padding accounts for accordion chevron (~36px) */}
              <Box
                px="md"
                py="xs"
                pr={52}
                style={{
                  borderBottom: '1px solid var(--mantine-color-gray-3)',
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr 130px 1.5fr 80px 80px',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">Admin</Text>
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">Impersonated User</Text>
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">Role</Text>
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">Started</Text>
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">Duration</Text>
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">Status</Text>
              </Box>

              <Accordion chevron={<ChevronDown size={16} />} variant="default">
                {entries.map((entry) => {
                  const personaConfig = PERSONA_LABELS[entry.impersonated_persona] || {
                    label: entry.impersonated_persona,
                    color: 'gray',
                  };

                  return (
                    <Accordion.Item key={entry.id} value={entry.id}>
                      <Accordion.Control px="md">
                        <Box
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 2fr 130px 1.5fr 80px 80px',
                            gap: 8,
                            alignItems: 'center',
                          }}
                        >
                          <Text size="sm" fw={500} truncate>
                            {entry.admin_name}
                          </Text>
                          <Box style={{ minWidth: 0 }}>
                            <Text size="sm" fw={500} truncate>
                              {entry.impersonated_name}
                            </Text>
                            <Text size="xs" c="dimmed" truncate>
                              {entry.impersonated_email}
                            </Text>
                          </Box>
                          <Box>
                            <Badge size="sm" variant="light" color={personaConfig.color}>
                              {personaConfig.label}
                            </Badge>
                          </Box>
                          <Text size="sm" truncate>
                            {formatDateTime(entry.started_at)}
                          </Text>
                          <Text size="sm">
                            {formatDuration(entry.started_at, entry.ended_at)}
                          </Text>
                          <Box>
                            <Badge
                              size="sm"
                              variant="light"
                              color={entry.ended_at ? 'gray' : 'red'}
                            >
                              {entry.ended_at ? 'Ended' : 'Active'}
                            </Badge>
                          </Box>
                        </Box>
                      </Accordion.Control>
                      <Accordion.Panel>
                        <SessionActivities sessionId={entry.id} />
                      </Accordion.Panel>
                    </Accordion.Item>
                  );
                })}
              </Accordion>

              {totalPages > 1 && (
                <Group justify="center" py="md">
                  <Pagination value={page} onChange={setPage} total={totalPages} />
                </Group>
              )}
            </>
          )}
        </Card>
      </Stack>
    </Container>
  );
}

export default ImpersonationLogPage;
